/**
 * @fileoverview Authentication context provider for managing user authentication state.
 * Provides authentication functionality using AWS Amplify Cognito service.
 */

import React, { createContext, useState, useContext, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

/**
 * @typedef {Object} AwsConfig
 * @property {Object} Auth - Authentication configuration
 * @property {Object} Auth.Cognito - Cognito-specific configuration
 * @property {string} Auth.Cognito.userPoolId - Cognito user pool ID
 * @property {string} Auth.Cognito.userPoolClientId - Cognito client ID
 * @property {Object} Auth.Cognito.loginWith - Login configuration
 * @property {Object} Auth.Cognito.loginWith.oauth - OAuth configuration
 */
const awsConfig = {
  Auth: {
    Cognito: {
      userPoolId: 'us-east-2_iIfwSsdCU',
      userPoolClientId: '1rv7iijlcgv4cortina322ntri',
      loginWith: {
        oauth: {
          domain: 'us-east-2iifwssdcu.auth.us-east-2.amazoncognito.com/login',
          scope: ['email', 'openid', 'phone'],
          redirectSignIn: 'https://d84l1y8p4kdic.cloudfront.net',
          redirectSignOut: 'https://d84l1y8p4kdic.cloudfront.net',
          responseType: 'code'
        }
      }
    }
  }
};

Amplify.configure(awsConfig);

/**
 * @typedef {Object} AuthContextType
 * @property {Object|null} user - Current authenticated user object
 * @property {boolean} loading - Loading state of authentication
 * @property {boolean} isAuthenticated - Whether a user is authenticated
 * @property {Function} signOut - Function to sign out the current user
 * @property {Function} getAccessToken - Function to get the current access token
 * @property {Function} checkUser - Function to check and update the current user
 */

/**
 * React context for authentication state and functions
 * @type {React.Context<AuthContextType>}
 */
const AuthContext = createContext({});

/**
 * Authentication provider component that manages auth state and provides auth functions
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {React.ReactElement} The rendered AuthProvider component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();

    /**
     * Event listener for auth state changes
     * @param {Object} data - Event data
     * @param {Object} data.payload - Event payload
     */
    const listener = (data) => {
      const { payload } = data;
      if (payload.event === 'signIn') {
        checkUser();
      }
      if (payload.event === 'signOut') {
        setUser(null);
      }
    };

    const hubListener = Hub.listen('auth', listener);
    return () => hubListener();
  }, []);

  /**
   * Checks and updates the current authenticated user
   * @async
   * @function checkUser
   * @returns {Promise<void>}
   */
  async function checkUser() {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups = session?.tokens?.idToken?.payload?.['cognito:groups'] || [];
      const userId = session?.tokens?.idToken?.payload?.['custom:UserID'] || 
                    session?.tokens?.idToken?.payload?.['cognito:username'] ||
                    session?.tokens?.idToken?.payload?.['sub'];
      
      // Add groups and isAdmin flag to user object
      // Note: Cognito groups can be in any case, so we normalize for checking
      const normalizedGroups = groups.map(g => g.toLowerCase());
      
      let userWithGroups = {
        ...currentUser,
        userId, // Add the custom:UserID to user object
        groups,
        isAdmin: normalizedGroups.includes('admin'),
        isManager: normalizedGroups.includes('manager'),
        canViewRevenue: normalizedGroups.includes('admin') // Only admins can view ALL revenue by default
      };

      // For non-admins (managers and employees), fetch employee record to get assigned clients and revenue permission
      if (!userWithGroups.isAdmin && userId) {
        try {
          const token = session.tokens.idToken.toString();
          const response = await fetch(`https://track.snapped.cc/api/employees/?active_only=true`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const employees = await response.json();
            // Find the current user in the employee list
            const currentEmployee = employees.find(emp => 
              emp.user_id === userId || 
              emp.user_id.toLowerCase() === userId.toLowerCase() ||
              emp.email === session?.tokens?.idToken?.payload?.['email']
            );
            
            if (currentEmployee) {
              userWithGroups.assignedClients = currentEmployee.clients || [];
              userWithGroups.role = currentEmployee.role || 'csm'; // Get role from employee record
              userWithGroups.organizationId = currentEmployee.organization_id; // Get organization
              
              // Check can_view_revenue field from employee record (per-employee override)
              // If not set, fall back to role-based defaults
              if (currentEmployee.can_view_revenue !== undefined) {
                // Use explicit per-employee setting
                userWithGroups.canViewRevenue = currentEmployee.can_view_revenue;
              } else {
                // Fall back to role-based defaults
                if (currentEmployee.role === 'admin') {
                  userWithGroups.canViewRevenue = true; // Regular admins can view all financial data
                } else {
                  userWithGroups.canViewRevenue = false; // Other roles: default to false
                }
              }
            } else {
              // If no employee record found, default role to CSM
              userWithGroups.role = 'csm';
              userWithGroups.canViewRevenue = false;
            }
          }
        } catch (error) {
          console.error('Error fetching employee data:', error);
          // If error, default to no revenue access and CSM role
          userWithGroups.role = 'csm';
          userWithGroups.canViewRevenue = false;
        }
      } else if (userWithGroups.isAdmin) {
        // Admins get admin role and full revenue access
        userWithGroups.role = 'admin';
        userWithGroups.canViewRevenue = true;
      }
      
      setUser(userWithGroups);
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Gets the current user's access token
   * @async
   * @function getAccessToken
   * @returns {Promise<string|null>} The access token or null if not available
   */
  const getAccessToken = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      return tokens.idToken.toString();
    } catch (error) {
      console.error('Error getting access token:', error);
      return null;
    }
  };

  /**
   * Signs out the current user
   * @async
   * @function handleSignOut
   * @returns {Promise<void>}
   */
  const handleSignOut = async () => {
    try {
      await signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    signOut: handleSignOut,
    getAccessToken,
    checkUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to access the auth context
 * @function useAuth
 * @returns {AuthContextType} The auth context value
 */
export const useAuth = () => {
  return useContext(AuthContext);
}; 